import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

import "./css/App.css";
import SideBar from "./components/sidebar/SideBar";
import Content from "./components/content/Content";
import Context from './context';
import { MicrosoftAuthProvider } from "./context/MicrosoftAuthContext";
import { ToastContainer } from 'react-toastify';
import withRouter from './withRouter';

const App = (props) => {
  // Mobile-first design
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const [selectedComponent, setSelectedComponent] = useState("Dashboard");
  
  const previousWidth = useRef(-1);
  const sidebarRef = useRef(null);

  /**
   * Update window width and mobile view state
   */
  const updateWidth = () => {
    const width = window.innerWidth;
    const widthLimit = 768; // Mobile width limit
    const isMobileView = width <= widthLimit;
    const wasMobile = previousWidth.current <= widthLimit;

    if (isMobileView !== wasMobile) {
      setIsOpen(!isMobileView); // Automatically close sidebar on mobile
      setIsMobile(isMobileView);
    }

    previousWidth.current = width;
  };

  /**
   * Handle click outside sidebar to close it on mobile
   */
  const handleClickOutside = (event) => {
    if (
      isMobile && 
      isOpen && 
      sidebarRef.current && 
      !sidebarRef.current.contains(event.target) &&
      !event.target.closest('.burger-menu-button')
    ) {
      setIsOpen(false);
    }
  };

  /**
   * Handle ESC key to close sidebar on mobile
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isMobile && isOpen) {
      setIsOpen(false);
    }
  };

  /**
   * Toggle sidebar visibility for mobile or collapse/expand for desktop
   */
  const toggle = () => {
    if (isMobile) {
      // On mobile, toggle open/close
      setIsOpen(prevState => !prevState);
    } else {
      // On desktop, toggle collapse/expand
      setIsCollapsed(prevState => !prevState);
      setIsOpen(true); // Ensure sidebar is open when toggling collapse
    }
  };

  /**
   * Update selected component
   */
  const handleComponentChange = (component) => {
    setSelectedComponent(component);
  };

  /**
   * Add event listeners
   */
  useEffect(() => {
    updateWidth();
    window.addEventListener("resize", updateWidth);
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", updateWidth);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobile, isOpen]);

  /**
   * Prevent body scroll when sidebar is open on mobile
   */
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }

    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [isMobile, isOpen]);

  const isLoginPage = props.location.pathname === "/login" || props.location.pathname === "/";

  return (
    <MicrosoftAuthProvider>
      <>
        <Context.Provider value={{}}>
          <div className={`App wrapper bg-FEFAF6 position-relative ${isMobile && isOpen ? 'sidebar-backdrop' : ''}`}>
            {/* Sidebar */}
            {!isLoginPage && (
              <div ref={sidebarRef}>
                <SideBar
                  isOpen={isOpen}
                  isCollapsed={isCollapsed}
                  toggle={toggle}
                  changeComponent={handleComponentChange}
                />
              </div>
            )}

            {/* Content */}
            <Content
              isLoginPage={isLoginPage}
              isOpen={isOpen}
              isCollapsed={isCollapsed}
              isMobile={isMobile}
              toggle={toggle}
              selectedComponent={selectedComponent}
            />
          </div>
        </Context.Provider>
        <ToastContainer position="top-right" autoClose={5000} />
      </>
    </MicrosoftAuthProvider>
  );
};

export default withRouter(App);
